// src/App.tsx
import React, { useState, useEffect, useRef } from "react";
import { StructureManagerDashboard } from "./components/StructureManagerDashboard";
import { PortalManagerDashboard } from "./components/PortalManagerDashboard";
import { AccessibilityPassport } from "./components/AccessibilityPassport";
import { AccessibilityPassport2 } from "./components/AccessibilityPassport2";
import { VerificationRequestForm } from "./components/VerificationRequestForm";
import { PortalMonitorView } from "./components/PortalMonitorView";
import { ImprovementSuggestions } from "./components/ImprovementSuggestions";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { getFirestore, collection, addDoc, Timestamp } from "firebase/firestore";
import { auth } from "./firebase/firebase";
import { getUserProfile, getStructuresForUser, getStructuresByIds, ensureUserProfileExists, getAccessCaps } from "./firebase/firestore";
import type { UserProfile } from "./firebase/firestore";
import { useAuth } from "./components/SessionProvider";
import { 
  handleOAuthRedirectResult, 
  hasStructureManagerAccess, 
  getManagedStructuresFromClaims,
  attemptSilentAuthIfNeeded,
  signInWithTravelinOAuthWithPrompt,
  normalizePromptParam,
} from "./firebase/oauth-auth";
import { fetchStructuresByPortals, hasPortalAccess, type PortalStructureCache } from "./utils/portalStructures";
// Removed test/debug side-effect imports for production cleanliness:
//   ./utils/testPortalFiltering
//   ./utils/debugFirestore
//   ./utils/testPrivatePublicMapping
//   ./utils/testPrivateStructureAccess
import idSlugData from "./data/structures-id-and-slug.updated.json";

import HomeView, { ClickRole } from "./components/HomeView";
import TravelersHub from "./components/TravelersHub";
import { VisitLanding } from "./components/VisitLanding";
import { PassportEmbed } from "./components/PassportEmbed";
import { syncVisitUser } from "./lib/visitApi";
import { toast } from "sonner";
import { updateSuggestionStatusSafe } from "./lib/suggestionsStatusWriter";
import { getPortalBranding } from "./utils/portalPriority";

type UserRole =
  | "login"
  | "structure-manager"
  | "portal-manager"
  | "accessibility-passport"
  | "accessibility-passport2"
  | "verification-request"
  | "portal-passport"
  | "portal-monitor"
  | "travelers-hub"
  | "improvement-suggestions"
  | "embed-passport"
  | "no-access";

type NavigationSource = 
  | "structure-manager"
  | "portal-manager" 
  | "travelers-hub"
  | null;

const VISIT_ENTRY_KEY = "visit:entry";
const VIEW_QUERY_KEY = "view";
const TRAVELERS_HUB_VIEW = "travelers-hub";

function shouldLogAccessDebug(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return localStorage.getItem('DEBUG_ACCESS_ROUTING') === '1';
  } catch {
    return false;
  }
}

function shouldLogAuthRoutingDebug(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return localStorage.getItem('DEBUG_AUTH_ROUTING') === '1';
  } catch {
    return false;
  }
}

function isEmbedPath(): boolean {
  try {
    return window.location.pathname === '/embed/passport';
  } catch {
    return false;
  }
}

function getEmbedParams(): { structureId?: string; portal?: string; theme?: 'blue' | 'brand' | 'orange'; language?: 'it' | 'en' | 'de' | 'fr'; size?: 'compact' | 'standard' | 'detailed' } {
  try {
    const url = new URL(window.location.href);
    return {
      structureId: url.searchParams.get('structureId') || undefined,
      portal: url.searchParams.get('portal') || undefined,
      theme: (url.searchParams.get('theme') as any) || 'blue',
      language: (url.searchParams.get('language') as any) || 'it',
      size: (url.searchParams.get('size') as any) || 'standard',
    };
  } catch {
    return {};
  }
}

function detectVisitEntryFromUrl(): boolean {
  try {
    const url = new URL(window.location.href);
    const prompt = normalizePromptParam(url.searchParams.get('prompt'));
    return (
      url.searchParams.get('fromVisit') === '1' ||
      url.searchParams.get('vb') === '1' ||
      url.searchParams.has('vbEmail') ||
      url.searchParams.get('source') === 'visit' ||
      prompt === 'none' || prompt === 'login' // Visit Bergamo links can include prompt=none/login (typo tolerant)
    );
  } catch {
    return false;
  }
}

function seedVisitFlowFlag(): boolean {
  try {
    const fromUrl = detectVisitEntryFromUrl();
    const stored = sessionStorage.getItem(VISIT_ENTRY_KEY) === '1';
    if (fromUrl) sessionStorage.setItem(VISIT_ENTRY_KEY, '1');
    return fromUrl || stored;
  } catch {
    return false;
  }
}

function getViewFromUrl(): UserRole | null {
  try {
    const url = new URL(window.location.href);
    const view = url.searchParams.get(VIEW_QUERY_KEY);
    if (view === TRAVELERS_HUB_VIEW) return "travelers-hub";
    return null;
  } catch {
    return null;
  }
}

function isNonEmptyArray<T>(val: unknown): val is T[] {
  return Array.isArray(val) && val.length > 0;
}

function canAccess(role: ClickRole, managedStructures: unknown, portals: unknown) {
  return role === "Structure Manager" ? isNonEmptyArray(managedStructures) : isNonEmptyArray(portals);
}

function NoAccessPage({ onGoLogin }: { onGoLogin: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg shadow-sm p-6 text-center">
        <h1 className="text-l font-semibold text-slate-900">Accesso non autorizzato</h1>
        <p className="mt-2 text-s text-slate-600">
          Il tuo account non ha i permessi per visualizzare questa dashboard.
        </p>
        <button
          type="button"
          onClick={onGoLogin}
          className="mt-4 inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-2 text-s font-medium text-white hover:bg-slate-800"
        >
          Torna al login
        </button>
      </div>
    </div>
  );
}

type AccessCaps = {
  isAdmin: boolean;
  isPortalManager: boolean;
  isStructureManager: boolean;
  portals: string[];
};

// Helper to manage suggestions in verification (persisted in session storage)
function loadVerificationInProgress(): Set<string> {
  try {
    const stored = sessionStorage.getItem('verifications-in-progress');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

function saveVerificationInProgress(ids: Set<string>) {
  try {
    sessionStorage.setItem('verifications-in-progress', JSON.stringify(Array.from(ids)));
  } catch {
    // ignore
  }
}

export default function App() {
  const [currentView, setCurrentView] = useState<UserRole>("login");
  const [navigationSource, setNavigationSource] = useState<NavigationSource>(null);
  const [portalActiveSection, setPortalActiveSection] = useState<"overview" | "details">("overview");
  const [verificationData, setVerificationData] = useState<{ suggestion: any; structureName: string; structureId?: string | null } | null>(null);
  const [verificationsInProgress, setVerificationsInProgress] = useState<Set<string>>(() => loadVerificationInProgress());
  const [selectedStructureData, setSelectedStructureData] = useState<any>(null);
  const [selectedPortalMonitor, setSelectedPortalMonitor] = useState<{ structureData: any; portalName: string } | null>(null);
  const [loginModalRole, setLoginModalRole] = useState<ClickRole | null>(null);

  const [user, setUser] = useState<any | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [managedStructures, setManagedStructures] = useState<any[]>([]);
  const [portals, setPortals] = useState<string[]>([]);
  const [managedStructureDocs, setManagedStructureDocs] = useState<any[]>([]);
  const [portalStructureCache, setPortalStructureCache] = useState<PortalStructureCache>({});
  const [portalStructuresError, setPortalStructuresError] = useState<{ code: string } | null>(null);
  const [caps, setCaps] = useState<AccessCaps | null>(null);
  const [homeMode, setHomeMode] = useState<"login" | "chooser">("login");
  const [auth0Arrival, setAuth0Arrival] = useState<boolean>(false);
  const quickAccessRef = useRef<boolean>(false);
  const [isVisitFlow, setIsVisitFlow] = useState<boolean>(() => seedVisitFlowFlag());
  type VisitSyncState = 'idle' | 'syncing' | 'ok' | 'not_provisioned' | 'disabled' | 'error';
  const [visitSyncState, setVisitSyncState] = useState<VisitSyncState>('idle');
  const [visitSyncErrorMessage, setVisitSyncErrorMessage] = useState<string | null>(null);
  const [isSubmittingVerification, setIsSubmittingVerification] = useState<boolean>(false);
  const visitFlowRef = useRef<boolean>(isVisitFlow);

  useEffect(() => {
    console.log('App version: 1.8.3');
  }, []);

  useEffect(() => {
    const initialView = getViewFromUrl();
    if (initialView) {
      safeSetView(initialView);
    }
  }, []);

  useEffect(() => {
    visitFlowRef.current = isVisitFlow;
  }, [isVisitFlow]);

  function safeSetView(nextView: UserRole, capsOverride?: AccessCaps | null) {
    const c = capsOverride ?? caps;
    if (nextView === "portal-manager" && !c?.isPortalManager && !c?.isAdmin) {
      setCurrentView("no-access");
      return;
    }
    setCurrentView(nextView);
  }

  const clearVisitFlow = () => {
    setIsVisitFlow(false);
    try {
      sessionStorage.removeItem(VISIT_ENTRY_KEY);
      sessionStorage.removeItem('auth0:auto-quick-started');
    } catch {
      // ignore
    }
  };

  const markVisitFlow = () => {
    setIsVisitFlow(true);
    try {
      sessionStorage.setItem(VISIT_ENTRY_KEY, '1');
    } catch {
      // ignore
    }
  };

  // Navigation strictly by explicit click
  const handleLogin = async (role: ClickRole) => {
    if (user) {
      if (canAccess(role, managedStructures, portals)) {
        if (role === "Portal Manager") {
          setPortalActiveSection("overview"); // Reset to overview for normal login
          safeSetView("portal-manager");
        } else {
          await prepareStructureAccessLogging(user, managedStructures);
          safeSetView("structure-manager");
        }
      } else {
        // Provide immediate feedback if the user lacks access
        const msg =
          role === "Structure Manager"
            ? "Your account doesn't have any managed structures assigned. Please contact an administrator."
            : "Your account isn't linked to any portals. Please contact an administrator.";
        alert(msg);
      }
      return;
    }
    // Not logged in: open modal but do NOT autoroute on auth change
    setLoginModalRole(role);
  };

  // Shared logic: compute available structures from mapping, fetch first from Firestore, set selection, and log once
  async function prepareStructureAccessLogging(u: any, managed: any[]) {
    try {
      const managedArr: any[] = Array.isArray(managed) ? managed : [];
      const mappingItems: any[] = Array.isArray((idSlugData as any)?.items) ? (idSlugData as any).items : [];
      const norm = (s: string) => String(s || '').trim().toLowerCase();
      const ensureJson = (s: string) => s.endsWith('.json') ? s : `${s}.json`;
      const idBySlug = new Map<string, string>();
      for (const it of mappingItems) {
        if (!it?.id) continue;
        const id = String(it.id);
        idBySlug.set(norm(id), id);
        if (it.slug) {
          const slug = String(it.slug);
          idBySlug.set(norm(slug), id);
          idBySlug.set(norm(ensureJson(slug)), id);
        }
      }
      const candidateIds = Array.from(new Set(managedArr
        .map((x) => {
          if (typeof x === 'string') {
            const key = norm(x);
            return idBySlug.get(key) || idBySlug.get(norm(ensureJson(x))) || String(x);
          }
          if (x && typeof x === 'object' && x.id) return String(x.id);
          return null;
        })
        .filter(Boolean) as string[]));

      let current = null as any;
      if (candidateIds.length) {
        const cacheMap: Record<string, any> = Object.fromEntries(
          (managedStructureDocs || []).map((d: any) => [String(d.id), d])
        );
        const cachedDocs = candidateIds.map((id) => cacheMap[String(id)]).filter(Boolean);
        if (cachedDocs.length) {
          current = cachedDocs[0];
          setSelectedStructureData(current);
        }
        const missing = candidateIds.filter((id) => !cacheMap[String(id)]);
        const fresh = missing.length ? await getStructuresByIds(missing) : [];
        const foundIds = new Set([...
          cachedDocs.map((d: any) => String(d.id)),
          ...(Array.isArray(fresh) ? fresh.map((d: any) => String(d.id)) : [])
        ]);
        const notFound = candidateIds.filter((id) => !foundIds.has(String(id)));
        if (notFound.length && shouldLogAccessDebug()) {
          console.debug('Managed structures not found in Firestore:', notFound);
        }
        if (!current && Array.isArray(fresh) && fresh.length) {
          current = fresh[0];
          setSelectedStructureData(current);
        }
        if (Array.isArray(fresh) && fresh.length) {
          setManagedStructureDocs((prev) => {
            const byId: Record<string, any> = Object.fromEntries((prev || []).map((d: any) => [String(d.id), d]));
            for (const d of fresh) byId[String(d.id)] = d;
            return Object.values(byId);
          });
        }
      } else if (Array.isArray(managedStructureDocs) && managedStructureDocs.length) {
        current = managedStructureDocs[0];
        setSelectedStructureData(current);
      }
      // Logging trimmed: avoid noisy console; keep authentication/Firebase logs elsewhere
    } catch (e) {
      // do not block navigation on logging failure
    }
  }

  // modal handlers (close modal; user stays on selector)
  const handleModalSignIn = () => setLoginModalRole(null);
  const handleModalCancel = () => setLoginModalRole(null);

  const handleLogout = () => {
    clearVisitFlow();
    signOut(auth).catch(() => {});
    setUser(null);
    setManagedStructures([]);
    setPortals([]);
    setPortalStructuresError(null);
    setHomeMode("login");
    setAuth0Arrival(false);
    safeSetView("login");
    setVerificationData(null);
    setSelectedStructureData(null);
    setSelectedPortalMonitor(null);
    setVisitSyncState('idle');
    setVisitSyncErrorMessage(null);
  };

  useEffect(() => {
    // Handle OAuth redirect result on app initialization
    const handleOAuthRedirect = async () => {
      try {
        const url = new URL(window.location.href);
        const hasRedirectParams = url.searchParams.has('code') || url.searchParams.has('error') || url.searchParams.has('state');
        
        // Check Firebase's internal redirect storage
        const firebaseRedirectKey = `firebase:pendingRedirect:AIzaSyBFxLnS2p-O8wxPCxFI-Rfq3AhVO6fQrVQ:[DEFAULT]`;
        const firebaseRedirectData = sessionStorage.getItem(firebaseRedirectKey);
        if (firebaseRedirectData) {
          try {
          } catch (e) {
          }
        }
        
        const buildSsoParams = () => {
          const params: Record<string, string> = {};
          const loginHint = url.searchParams.get('login_hint') || url.searchParams.get('email') || url.searchParams.get('vbEmail');
          const organization = url.searchParams.get('organization');
          if (loginHint) params.login_hint = loginHint;
          if (organization) params.organization = organization;
          return params;
        };

        // 1) ALWAYS try to consume redirect result first, before any new redirect attempts.
        // Firebase stores redirect state internally; even if URL params are gone, the state may still be present.
        let oauthResult = await handleOAuthRedirectResult();
        
        // 2) Remove ?prompt=login from URL after attempting consumption to prevent redirect loops
        // (Auth0 redirects back WITH ?prompt=login, which would trigger infinite redirects)
        const promptParam = normalizePromptParam(url.searchParams.get('prompt'));
        if (promptParam && !oauthResult) {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('prompt');
          window.history.replaceState({}, '', newUrl.toString());
        }
        
        if (oauthResult) {
          setAuth0Arrival(true);
          
          // Check if user has structure manager access
          const hasAccess = await hasStructureManagerAccess();
          
          if (hasAccess) {
            // Automatically navigate to structure manager dashboard
            safeSetView("structure-manager");
            
            // Try to preselect a structure immediately for smoother UX parity with email/password flow
            try {
              const ms = await getManagedStructuresFromClaims();
              if (Array.isArray(ms) && ms.length && auth.currentUser) {
                await prepareStructureAccessLogging(auth.currentUser, ms);
              }
            } catch (err) {
            }
            if (visitFlowRef.current) {
              clearVisitFlow();
            }
          }
        } else {
          // No redirect result found - check if we should start a new flow
          const autoQuickGuard = 'auth0:auto-quick-started';
          // promptParam already declared above after consuming redirect result
          const shouldAutoQuick = visitFlowRef.current && !auth.currentUser && sessionStorage.getItem(autoQuickGuard) !== '1';

          // If silent auth failed with login_required, trigger a single interactive login attempt (SSO-friendly)
          const silentFailed = (() => {
            try { return sessionStorage.getItem('auth0:silent-failed') === '1'; } catch { return false; }
          })();

          if (shouldAutoQuick) {
            markVisitFlow();
            sessionStorage.setItem(autoQuickGuard, '1');
            const ssoParams = buildSsoParams();
            await signInWithTravelinOAuthWithPrompt(promptParam, ssoParams);
            return;
          }

          // 2) If arriving with ?prompt=login explicitly, trigger a single interactive login.
          if (promptParam === 'login' && !auth.currentUser) {
            const GUARD = 'auth0:interactive-started';
            // If guard is already set, check if we're stuck (no redirect result but guard is set)
            if (sessionStorage.getItem(GUARD) === '1') {
              // Stuck state detection: guard is set but getRedirectResult returned nothing
              // This can happen if redirect was interrupted or state expired
              if (!oauthResult) {
                sessionStorage.removeItem(GUARD);
                // Fall through to retry the redirect
              } else {
                return;
              }
            }
            sessionStorage.setItem(GUARD, '1');
            const ssoParams = buildSsoParams();
            await signInWithTravelinOAuthWithPrompt('login', ssoParams);
            return;
          }

          // 3) If arriving with ?prompt=none (or typo variants) and no local Firebase session, attempt silent auth now.
          //    This may redirect away immediately; if it doesn't (guarded), we can optionally start interactive.
          if (promptParam === 'none' && !auth.currentUser) {
            const { started } = await attemptSilentAuthIfNeeded();
            // If silent was already attempted in this tab, skip auto-interactive to avoid redirect loops
            if (!started && sessionStorage.getItem('auth0:silent-attempted') === '1') {
              return;
            }
            if (!started) {
              // Silent wasn't started (guard or not requested anymore).
              // Optional auto-fallback to interactive login can be disabled via:
              //   - URL:  ?autoLogin=0  or  ?noAutoLogin=1
              //   - Env:  VITE_AUTH_AUTO_LOGIN=false
              const autoLoginEnv = (import.meta as any)?.env?.VITE_AUTH_AUTO_LOGIN !== 'false';
              const noAutoParam = url.searchParams.get('autoLogin') === '0' || url.searchParams.get('noAutoLogin') === '1';
              const shouldAutoLogin = autoLoginEnv && !noAutoParam;
              if (shouldAutoLogin) {
                const GUARD = 'auth0:interactive-started';
                if (sessionStorage.getItem(GUARD) !== '1') {
                  sessionStorage.setItem(GUARD, '1');
                  const ssoParams = buildSsoParams();
                  await signInWithTravelinOAuthWithPrompt(undefined, ssoParams);
                }
              } else {
              }
            }
          } else if (silentFailed && !auth.currentUser) {
            const GUARD = 'auth0:interactive-started';
            if (sessionStorage.getItem(GUARD) !== '1') {
              sessionStorage.setItem(GUARD, '1');
              const ssoParams = buildSsoParams();
              await signInWithTravelinOAuthWithPrompt(undefined, ssoParams);
            }
          }
        }
      } catch (error) {
      }
    };

    // Call OAuth redirect handler
    handleOAuthRedirect();

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUser(u);
        setVisitSyncState('idle');
        setVisitSyncErrorMessage(null);

        // Visit Bergamo: ensure user is provisioned in NextAPI before continuing (only for OIDC Visit users)
        let claims: any = {};
        try {
          const tokenResult = await u.getIdTokenResult();
          claims = (tokenResult?.claims as any) || {};
          
          // Check if user is disabled (applies to all users, not just Visit)
          const disabled = (tokenResult.claims as any)?.disabled;
          if (disabled === true) {
            setVisitSyncState('disabled');
            setVisitSyncErrorMessage(null);
            safeSetView('login');
            setUserProfile(null);
            setPortals([]);
            setManagedStructures([]);
            setManagedStructureDocs([]);
            setPortalStructureCache({});
            setPortalStructuresError(null);
            return;
          }
          
          const signInProvider = (tokenResult.claims as any)?.firebase?.sign_in_provider;
          const isVisitUser = typeof signInProvider === 'string'
            && (signInProvider === 'oidc.visit-bergamo' || signInProvider === 'oidc.visitbergamo' || signInProvider === 'oidc.travelinauth' || signInProvider.startsWith('oidc.'));
          if (typeof signInProvider === 'string' && signInProvider.startsWith('oidc.')) {
            setAuth0Arrival(true);
          }

          if (!isVisitUser) {
            // Non-Visit user - skip sync and continue normally
            setVisitSyncState('ok');
          } else {
            // Visit user - perform sync
            setVisitSyncState('syncing');
            const token = tokenResult.token || (await u.getIdToken());
            const syncRes = await syncVisitUser(token);
            if (!syncRes.ok) {
              const errText = 'errorText' in syncRes ? syncRes.errorText : 'unknown error';
              setVisitSyncState('error');
              setVisitSyncErrorMessage(`Visit sync failed: ${errText || 'unknown error'}`);
              setUserProfile(null);
              setPortals([]);
              setManagedStructures([]);
              setManagedStructureDocs([]);
              setPortalStructureCache({});
              setPortalStructuresError(null);
              safeSetView("login");
              return;
            }
            if (!syncRes.provisioned) {
              setVisitSyncState('not_provisioned');
              setVisitSyncErrorMessage(null);
              setUserProfile(null);
              setPortals([]);
              setManagedStructures([]);
              setManagedStructureDocs([]);
              setPortalStructureCache({});
              setPortalStructuresError(null);
              safeSetView("login");
              return;
            }
            if (syncRes.needsTokenRefresh) {
              try {
                await u.getIdToken(true);
                await u.getIdTokenResult(true);
              } catch {
                // ignore refresh errors; downstream will handle auth state
              }
            }
            setVisitSyncState('ok');
            setVisitSyncErrorMessage(null);
          }
        } catch (syncErr: any) {
          setVisitSyncState('error');
          setVisitSyncErrorMessage(`Visit sync failed: ${syncErr?.message || 'unknown error'}`);
          setUserProfile(null);
          setPortals([]);
          setManagedStructures([]);
          setManagedStructureDocs([]);
          setPortalStructureCache({});
          setPortalStructuresError(null);
          safeSetView("login");
          return;
        }

        // Guarantee Firestore user profile doc exists (provisions from claims if missing)
        await ensureUserProfileExists(u.uid);
        const profile: UserProfile | null = await getUserProfile(u.uid, u.email);
        setUserProfile(profile);
        const portalsArr = Array.isArray(profile?.portals) ? profile!.portals : [];
        setPortals(portalsArr);
  
        // Get managed structures from both Firestore and OAuth claims
        const firestoreStructures = await getStructuresForUser(u.uid, u.email);
        const oauthStructures = await getManagedStructuresFromClaims();
        
        // Combine structures from both sources, giving preference to OAuth claims
        const combinedStructures = [...new Set([...oauthStructures, ...(Array.isArray(firestoreStructures) ? firestoreStructures : [])])];
        const msArr = Array.isArray(combinedStructures) ? combinedStructures : [];
        setManagedStructures(msArr);

        const hasAnyStructures = msArr.length > 0;
        const caps = getAccessCaps(claims, profile);
        setCaps(caps);
        
        if (shouldLogAuthRoutingDebug()) {
          console.debug('Auth state changed - User capabilities:', {
            uid: u.uid,
            email: u.email,
            isPortalManager: caps.isPortalManager,
            isStructureManager: caps.isStructureManager,
            isAdmin: caps.isAdmin,
            portals: caps.portals,
            role: profile?.role,
            currentView,
            managedStructures: msArr.length
          });
        }
        
        const wasLogin = currentView === "login";
        const shouldAutoRoute = wasLogin;
        if (shouldAutoRoute) {
          quickAccessRef.current = false;
          // Auto-route Portal Managers to portal-manager dashboard
          if (caps.isPortalManager) {
            if (shouldLogAuthRoutingDebug()) {
              console.debug('Auto-routing Portal Manager to portal-manager dashboard');
            }
            setPortalActiveSection("overview");
            safeSetView("portal-manager", caps);
          } else if (hasAnyStructures) {
            if (shouldLogAuthRoutingDebug()) {
              console.debug('Auto-routing Structure Manager to structure-manager dashboard');
            }
            // Structure managers go to structure-manager
            await prepareStructureAccessLogging(u, msArr);
            safeSetView("structure-manager", caps);
          } else {
            if (shouldLogAuthRoutingDebug()) {
              console.debug('User has no managed structures or portals - staying on login');
            }
          }
          if (visitFlowRef.current) {
            clearVisitFlow();
          }
        } else if (visitFlowRef.current && wasLogin && !hasAnyStructures) {
          // Visit flow but no managed structures: exit landing so user can see the normal selector
          clearVisitFlow();
        }
        // Prefetch all managed structure documents by document ID (mapping-aware)
        try {
          const managedArr: any[] = Array.isArray(msArr) ? msArr : [];
          const mappingItems: any[] = Array.isArray((idSlugData as any)?.items) ? (idSlugData as any).items : [];
          const norm = (s: string) => String(s || '').trim().toLowerCase();
          const ensureJson = (s: string) => s.endsWith('.json') ? s : `${s}.json`;
          const idBySlug = new Map<string, string>();
          for (const it of mappingItems) {
            if (!it?.id) continue;
            const id = String(it.id);
            idBySlug.set(norm(id), id);
            if (it.slug) {
              const slug = String(it.slug);
              idBySlug.set(norm(slug), id);
              idBySlug.set(norm(ensureJson(slug)), id);
            }
          }
          const candidateIds = Array.from(new Set(managedArr
            .map((x) => {
              if (typeof x === 'string') {
                const key = norm(x);
                return idBySlug.get(key) || idBySlug.get(norm(ensureJson(x))) || String(x);
              }
              if (x && typeof x === 'object' && x.id) return String(x.id);
              return null;
            })
            .filter(Boolean) as string[]));
          const docs = candidateIds.length ? await getStructuresByIds(candidateIds) : [];
          const foundIds = new Set((docs || []).map((d: any) => String(d.id)));
          const notFound = candidateIds.filter((id) => !foundIds.has(String(id)));
          if (notFound.length && shouldLogAccessDebug()) {
            console.debug('Managed structures not found in Firestore:', notFound);
          }
          setManagedStructureDocs(Array.isArray(docs) ? docs : []);
        } catch (_) {
          setManagedStructureDocs([]);
        }

        // Cache portal structures if user has portal access
        if (hasPortalAccess(profile, portalsArr)) {
          try {
            setPortalStructuresError(null);
            const cache = await fetchStructuresByPortals(portalsArr);
            setPortalStructureCache(cache);
          } catch (error: any) {
            if (error?.code === 'permission-denied' || error?.code === 'missing-index' || error?.code === 'unauthenticated') {
              setPortalStructuresError({ code: String(error.code) });
            } else {
              setPortalStructuresError(null);
            }
            setPortalStructureCache({});
          }
        } else {
          setPortalStructureCache({});
          setPortalStructuresError(null);
        }

        // If user just signed in from the modal, auto-navigate when access is allowed
        if (loginModalRole) {
          setLoginModalRole(null); // close modal
          if (canAccess(loginModalRole, msArr, portalsArr)) {
            if (loginModalRole === "Portal Manager") {
              setPortalActiveSection("overview"); // Reset to overview for modal login
              safeSetView("portal-manager");
            } else {
              await prepareStructureAccessLogging(u, msArr);
              safeSetView("structure-manager");
            }
          }
        }
      } else {
        setUser(null);
        setUserProfile(null);
        setPortals([]);
        setManagedStructures([]);
        setManagedStructureDocs([]);
        setPortalStructureCache({});
        setPortalStructuresError(null);
        setHomeMode("login");
        setAuth0Arrival(false);
        safeSetView("login");
        setVisitSyncState('idle');
        setVisitSyncErrorMessage(null);
      }
    });
    return () => unsub();
  }, [loginModalRole]);

  const handleGoToPassport = (structureSlugOrData?: any, source?: NavigationSource) => {
    setSelectedStructureData(structureSlugOrData ?? null);
    setNavigationSource(source || null);
    safeSetView("accessibility-passport");
  };

  const handleGoToPassport2 = (structureSlugOrData?: any, source?: NavigationSource) => {
    setSelectedStructureData(structureSlugOrData ?? null);
    setNavigationSource(source || null);
    safeSetView("accessibility-passport2");
  };
  const handleBackToDashboard = () => {
    // Navigate back based on where we came from
    if (navigationSource === "travelers-hub") {
      safeSetView("travelers-hub");
    } else if (navigationSource === "portal-manager") {
      safeSetView("portal-manager");
    } else {
      // Default to structure manager
      safeSetView("structure-manager");
    }
    setVerificationData(null);
    setNavigationSource(null);
  };

  const handleBackToPortalDashboard = () => {
    setPortalActiveSection("details"); // Set to details section when coming back from passport
    safeSetView("portal-manager");
    setSelectedStructureData(null);
    setSelectedPortalMonitor(null);
    setNavigationSource(null);
  };

  const handleRequestVerification = (suggestion: any, structureName: string, structureId?: string) => {
    setVerificationData({ suggestion, structureName, structureId: structureId ?? null });
    safeSetView("verification-request");
  };

  const normalizeCategoryValue = (val: unknown) => String(val ?? '').trim().toLowerCase();
  const normalizeStatusValue = (val: unknown) => String(val ?? '').trim().toLowerCase();

  const resolveVerificationCategory = (suggestion: any): 'motoria' | 'visiva' | 'uditiva' | 'cognitiva' | null => {
    const raw = normalizeCategoryValue(
      suggestion?.category ??
      suggestion?.categoria ??
      suggestion?.accessibilityCategory ??
      suggestion?.area ?? ''
    );
    if (!raw) return null;
    if (raw.includes('motor')) return 'motoria';
    if (raw.includes('visiv') || raw.includes('sensor')) return 'visiva';
    if (raw.includes('udit') || raw.includes('audio')) return 'uditiva';
    if (raw.includes('cognitiv')) return 'cognitiva';
    return null;
  };

  const resolveVerificationStructureId = () => {
    if (verificationData?.structureId) return String(verificationData.structureId);
    if (typeof selectedStructureData === 'string') return String(selectedStructureData);
    if (selectedStructureData && typeof selectedStructureData === 'object') {
      return (
        (selectedStructureData.id && String(selectedStructureData.id)) ||
        (selectedStructureData.structureId && String(selectedStructureData.structureId)) ||
        (selectedStructureData.backend_id && String(selectedStructureData.backend_id)) ||
        null
      );
    }
    return null;
  };

  const handleVerificationSubmit = async (submissionData: any) => {
    // Update the suggestion status to "verifica_in_corso" in session immediately (persists in this session)
    if (verificationData?.suggestion?.id) {
      const suggestionKey = `${verificationData.structureId}:${verificationData.suggestion.id}`;
      const newSet = new Set(verificationsInProgress);
      newSet.add(suggestionKey);
      setVerificationsInProgress(newSet);
      saveVerificationInProgress(newSet);
    }
    
    // Update the suggestion status to "verifica_in_corso" using the proper status update mechanism
    if (verificationData?.structureId && verificationData?.suggestion?.id) {
      try {
        await updateSuggestionStatusSafe({
          structureId: verificationData.structureId,
          suggestionId: String(verificationData.suggestion.id),
          newStatus: 'verifica_in_corso',
          prevStatus: verificationData.suggestion.status || 'todo',
          source: 'verification_request_submit'
        });
      } catch (error) {
        // Silently fail - status is already saved in session
        console.error('Failed to update verification status:', error);
      }
    }
    
    // Close the form and go back to dashboard
    setVerificationData(null);
    handleBackToDashboard();
  };

  const handleViewPortalPassport = (structureSlug: string) => {
    setSelectedStructureData(structureSlug);
    setNavigationSource("portal-manager");
    safeSetView("accessibility-passport2");
  };

  const handleViewPortalMonitor = (structureData: any, portalName: string) => {
    setSelectedPortalMonitor({ structureData, portalName });
    safeSetView("portal-monitor");
  };

  const handleViewImprovementSuggestions = () => {
    safeSetView("improvement-suggestions");
  };

  const renderVisitSyncNotice = () => {
    if (visitSyncState === 'not_provisioned') {
      return (
        <div className="w-full bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 text-s text-center">
          Visit user not provisioned yet
        </div>
      );
    }
    if (visitSyncState === 'error') {
      return (
        <div className="w-full bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-s text-center">
          {visitSyncErrorMessage || 'Visit sync failed'}
        </div>
      );
    }
    if (visitSyncState === 'disabled') {
      return (
        <div className="w-full bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-s text-center">
          Your account is disabled
        </div>
      );
    }
    return null;
  };

  const isVisitUserSkin = (() => {
    const hasUserPortals = Array.isArray(portals) && portals.length > 0;
    const branding = getPortalBranding(portals, auth0Arrival === true && !hasUserPortals);
    return branding === "visit-bergamo";
  })();
  const passportBranding = isVisitUserSkin ? "visit-bergamo" : "default";

  // === Embed Route (no auth, no app layout) ===
  if (isEmbedPath()) {
    const embedParams = getEmbedParams();
    return <PassportEmbed {...embedParams} />;
  }

  // === Views ===
  if (currentView === "structure-manager") {
    return (
      <StructureManagerDashboard
        onLogout={handleLogout}
        onGoToPassport={(structureSlugOrData) => handleGoToPassport(structureSlugOrData, "structure-manager")}
        onGoToPassport2={(structureSlugOrData) => handleGoToPassport2(structureSlugOrData, "structure-manager")}
        selectedStructureData={selectedStructureData}
        onRequestVerification={handleRequestVerification}
        verificationsInProgress={verificationsInProgress}
        user={user}
        managedStructures={managedStructures}
        portals={portals}
        cachedStructures={managedStructureDocs}
        onGoHome={() => {
          setHomeMode("chooser");
          safeSetView("login");
        }}
        onSwitchToPortal={(caps?.isPortalManager || caps?.isAdmin) && managedStructures.length > 0 ? () => safeSetView("portal-manager") : undefined}
      />
    );
  }

  if (currentView === "portal-manager" && !caps?.isPortalManager && !caps?.isAdmin) {
    return <NoAccessPage onGoLogin={() => safeSetView("login")} />;
  }

  if (currentView === "portal-manager") {
    return (
      <PortalManagerDashboard
        onLogout={handleLogout}
        onViewPassport={handleViewPortalPassport}
        onViewMonitor={handleViewPortalMonitor}
        onViewImprovementSuggestions={handleViewImprovementSuggestions}
        user={user}
        managedStructures={managedStructures}
        portals={portals}
        portalStructureCache={portalStructureCache}
        portalStructuresError={portalStructuresError}
        onGoHome={() => {
          setPortalActiveSection("overview"); // Reset to overview when going home
          setHomeMode("chooser");
          safeSetView("login");
        }}
        onSwitchToStructure={(caps?.isPortalManager || caps?.isAdmin) && managedStructures.length > 0 ? () => safeSetView("structure-manager") : undefined}
        initialActiveSection={portalActiveSection}
        onRetry={async () => {
          if (portals.length > 0) {
            try {
              setPortalStructuresError(null);
              const cache = await fetchStructuresByPortals(portals);
              setPortalStructureCache(cache);
            } catch (error: any) {
              if (error?.code === 'permission-denied' || error?.code === 'missing-index' || error?.code === 'unauthenticated') {
                setPortalStructuresError({ code: String(error.code) });
              } else {
                setPortalStructuresError(null);
              }
              setPortalStructureCache({});
            }
          }
        }}
      />
    );
  }

  if (currentView === "accessibility-passport") {
    return (
      <AccessibilityPassport
        onBack={handleBackToDashboard}
        structureSlug={selectedStructureData}
        navigationSource={navigationSource}
        branding={passportBranding}
      />
    );
  }

  if (currentView === "accessibility-passport2") {
    return (
      <AccessibilityPassport2
        onBack={handleBackToDashboard}
        structureSlug={selectedStructureData}
        navigationSource={navigationSource}
        managedStructureIds={managedStructures}
        branding={passportBranding}
      />
    );
  }

  if (currentView === "portal-passport") {
    return (
      <AccessibilityPassport2
        onBack={handleBackToPortalDashboard}
        structureSlug={selectedStructureData}
        navigationSource={navigationSource}
        managedStructureIds={managedStructures}
        branding={passportBranding}
      />
    );
  }

  if (currentView === "portal-monitor") {
    return <PortalMonitorView onBack={handleBackToPortalDashboard} portalMonitor={selectedPortalMonitor} />;
  }

  if (currentView === "verification-request" && verificationData) {
    return (
      <VerificationRequestForm
        suggestion={verificationData.suggestion}
        structureName={verificationData.structureName}
        onBack={handleBackToDashboard}
        onSubmit={handleVerificationSubmit}
      />
    );
  }

  if (currentView === "improvement-suggestions") {
    return <ImprovementSuggestions onBack={handleBackToPortalDashboard} />;
  }

  if (currentView === "travelers-hub") {
    return (
      <TravelersHub
        onBack={() => safeSetView("login")}
        onOpenPassport={(slug) => handleGoToPassport(slug, "travelers-hub")}
      />
    );
  }

  if (currentView === "no-access") {
    return <NoAccessPage onGoLogin={() => safeSetView("login")} />;
  }

  if (isVisitFlow && currentView === "login" && !user) {
    return (
      <div className="min-h-screen flex flex-col">
        {renderVisitSyncNotice()}
        <VisitLanding />
      </div>
    );
  }

  // Home View (login selector + modal)
  return (
    <div className="min-h-screen flex flex-col">
      {renderVisitSyncNotice()}
      <HomeView
        onLogin={handleLogin}
        loginModalRole={loginModalRole}
        onModalCancel={handleModalCancel}
        onModalSignIn={handleModalSignIn}
        currentUser={user}
        userProfile={userProfile}
        caps={caps}
        homeMode={homeMode}
        hasManagedStructures={managedStructures.length > 0}
        auth0Only={auth0Arrival}
        onQuickAccess={() =>
          new Promise<void>((resolve) => {
            quickAccessRef.current = true;
            setTimeout(() => {
              safeSetView("structure-manager");
              resolve();
            }, 150);
          })
        }
        onOpenTravelersHub={() => safeSetView("travelers-hub")}
        onLogout={handleLogout}
        showAuth0Test={import.meta.env.DEV} // Show Auth0 test in development mode
      />
    </div>
  );
}
